import React, { useCallback, useMemo, useRef } from "react";
import codevexLogo from "../Icons/codevex.png";
import SnakeGame from "./SnakeGame";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export default function MemoWindow({
  title,
  value,
  x,
  y,
  width,
  height,
  readOnly = false,
  canRename = true,
  variant = "memo",
  onChange,
  onRename,
  onFrameChange,
  onClose,
}) {
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
    active: false,
  });

  const resizeRef = useRef({
    startX: 0,
    startY: 0,
    startW: 0,
    startH: 0,
    active: false,
  });

  const onTitlePointerDown = useCallback(
    (e) => {
      // Only left click / primary touch
      if (e.button != null && e.button !== 0) return;
      // Don't initiate a drag when interacting with controls in the title bar
      if (e.target?.closest?.("button,input,textarea,select,a")) return;
      e.preventDefault();

      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startLeft: x,
        startTop: y,
        active: true,
      };

      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [x, y]
  );

  const onTitlePointerMove = useCallback(
    (e) => {
      if (!dragRef.current.active) return;

      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;

      const nextX = dragRef.current.startLeft + dx;
      const nextY = dragRef.current.startTop + dy;

      // Keep it roughly within the viewport (menu bar is ~28px)
      const maxX = Math.max(8, window.innerWidth - 120);
      const maxY = Math.max(36, window.innerHeight - 80);

      onFrameChange?.({
        x: clamp(nextX, 8, maxX),
        y: clamp(nextY, 36, maxY),
      });
    },
    [onFrameChange]
  );

  const onTitlePointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  const onResizePointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startW: width,
        startH: height,
        active: true,
      };

      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [width, height]
  );

  const onResizePointerMove = useCallback(
    (e) => {
      if (!resizeRef.current.active) return;

      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;

      const minW = 320;
      const minH = 220;
      const maxW = Math.max(minW, window.innerWidth - x - 12);
      const maxH = Math.max(minH, window.innerHeight - y - 12);

      onFrameChange?.({
        w: clamp(resizeRef.current.startW + dx, minW, maxW),
        h: clamp(resizeRef.current.startH + dy, minH, maxH),
      });
    },
    [onFrameChange, x, y]
  );

  const onResizePointerUp = useCallback((e) => {
    if (!resizeRef.current.active) return;
    resizeRef.current.active = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  const aboutParagraphs = useMemo(() => {
    if (variant !== "about") return [];
    return String(value || "")
      .split(/\n\s*\n/g)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [variant, value]);

  return (
    <section
      className="mac-window"
      role="dialog"
      aria-label={title}
      style={{ left: x, top: y, width, height }}
    >
      <div
        className="mac-windowTitleBar"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={onTitlePointerUp}
      >
        <button
          type="button"
          className="mac-windowClose"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          aria-label={`Close ${title}`}
          title="Close"
        >
          ×
        </button>
        <div className="mac-windowTitle" title={title}>
          {title}
        </div>
        <div className="mac-windowSpacer" aria-hidden="true" />
      </div>

      <div
        className={
          variant === "about" ? "mac-windowBody mac-windowBody--about" : "mac-windowBody"
        }
      >
        {variant === "snake" ? (
          <div className="mac-snakeBody" onPointerDown={(e) => e.stopPropagation()}>
            <SnakeGame isActive={true} />
          </div>
        ) : variant !== "about" ? (
          <>
            <label className="mac-fieldRow">
              <span className="mac-fieldLabel">Name</span>
              <input
                className="mac-fieldInput"
                value={title}
                readOnly={!canRename}
                disabled={!canRename}
                onChange={(e) => {
                  if (!canRename) return;
                  onRename?.(e.target.value);
                }}
                spellCheck="false"
                inputMode="text"
                onPointerDown={(e) => e.stopPropagation()}
              />
            </label>
            <textarea
              className="mac-memoTextarea"
              value={value}
              readOnly={readOnly}
              onChange={(e) => {
                if (readOnly) return;
                onChange(e.target.value);
              }}
              placeholder={readOnly ? "" : "Type your memo…"}
              spellCheck="false"
              onPointerDown={(e) => e.stopPropagation()}
            />
          </>
        ) : (
          <div className="mac-aboutBody" onPointerDown={(e) => e.stopPropagation()}>
            <img className="mac-aboutLogo" src={codevexLogo} alt="CodeVex" />
            <div className="mac-aboutText">
              {aboutParagraphs.map((p, idx) => {
                const email = "contact@codevex.xyz";
                if (p.includes(email)) {
                  const [before, after] = p.split(email);
                  return (
                    <p key={idx}>
                      {before}
                      <a className="mac-link" href={`mailto:${email}`}>
                        {email}
                      </a>
                      {after}
                    </p>
                  );
                }
                return <p key={idx}>{p}</p>;
              })}
            </div>
          </div>
        )}
      </div>

      <div
        className="mac-resizeHandle"
        role="presentation"
        aria-hidden="true"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        title="Resize"
      />
    </section>
  );
}

